import {CalloutIcon, type IconName, Link} from '@wandb/components';
import {NavLink} from 'react-router-dom';

import {catalogHref, shellRouteClaimTable} from '../routeClaims';
import heroCards from './hero-cards.webp';
import css from './HomePage.module.css';
import {homeProducts} from './products';
import {customerStories, tutorials} from './stories';

const YOUTUBE_CHANNEL_HREF = 'https://www.youtube.com/@WeightsBiases';

const StoryCard = ({
  href,
  thumbnail,
  title,
}: {
  href: string;
  thumbnail: string;
  title: string;
}) => (
  <article className={css.tutorialCard}>
    <img className={css.thumbnail} src={thumbnail} alt="" />
    <h3 className={css.tutorialTitle}>
      <Link
        variant="secondary"
        href={href}
        target="_blank"
        rel="noopener noreferrer">
        {title}
      </Link>
    </h3>
  </article>
);

const ProductCardContent = ({
  description,
  icon,
  label,
  unavailable,
}: {
  description: string;
  icon: IconName;
  label: string;
  unavailable: boolean;
}) => (
  <>
    <CalloutIcon icon={icon} color="blue" />
    <span className={css.productCopy}>
      <span className={css.productLabel}>{label}</span>
      <span className={css.productDescription}>{description}</span>
      {unavailable ? (
        <span className={css.productStatus}>Coming soon</span>
      ) : null}
    </span>
  </>
);

export const HomePage = () => (
  <div className={css.home}>
    <div className={css.content}>
      <section className={css.hero} aria-labelledby="forge-home-heading">
        <div className={css.heroText}>
          <h1 id="forge-home-heading">
            Build, train, evaluate, and run in one connected environment.
          </h1>
          <p>
            CoreWeave Forge is where your model or agent keeps improving after
            deployment. What happens in production flows straight into what gets
            built next.
          </p>
        </div>
        <img
          className={css.heroArtwork}
          src={heroCards}
          alt=""
          aria-hidden="true"
        />
      </section>

      <section aria-labelledby="explore-products-heading">
        <h2 id="explore-products-heading" className={css.sectionHeading}>
          Explore our products
        </h2>
        <ul aria-label="Forge products" className={css.productGrid}>
          {homeProducts.map(product => {
            const href = catalogHref(product, shellRouteClaimTable());
            const content = (
              <ProductCardContent
                description={product.description}
                icon={product.icon}
                label={product.label}
                unavailable={!href}
              />
            );

            return (
              <li key={product.id} className={css.productListItem}>
                {href ? (
                  <NavLink className={css.productCard} to={href}>
                    {content}
                  </NavLink>
                ) : (
                  <div className={css.productCardUnavailable}>{content}</div>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      <section aria-labelledby="tutorials-heading">
        <div className={css.sectionHeader}>
          <h2 id="tutorials-heading" className={css.sectionHeading}>
            Step-by-step tutorials &amp; demos
          </h2>
          <Link
            href={YOUTUBE_CHANNEL_HREF}
            target="_blank"
            rel="noopener noreferrer">
            View more on YouTube
          </Link>
        </div>
        <div className={css.tutorialGrid}>
          {tutorials.map(story => (
            <StoryCard key={story.href} {...story} />
          ))}
        </div>
      </section>

      <section aria-labelledby="customer-stories-heading">
        <div className={css.sectionHeader}>
          <h2 id="customer-stories-heading" className={css.sectionHeading}>
            Customer &amp; partner stories
          </h2>
          <Link
            href="https://wandb.ai/site/customers/"
            target="_blank"
            rel="noopener noreferrer">
            View more customer stories
          </Link>
        </div>
        <div className={css.tutorialGrid}>
          {customerStories.map(story => (
            <StoryCard key={story.href} {...story} />
          ))}
        </div>
      </section>
    </div>
  </div>
);
